import { fetchAppealList } from "../../../api/postCall/fetchAppealList";
import { CaseAppealListRow } from "../../../data-model/caseAppealsTable";
import { logError } from "../../../utils/customLogger";
import { AppealListFilterPayload } from "../hooks/useFilterPayload";
import { CaseAppealsTableState } from "../hooks/useCaseAppealsTableData";

/**
 * Fetches the next page of appeal list data for Case Appeals table pagination.
 *
 * @param payload - Page, limit, and filters with incremented page number
 * @param setCaseAppealsState - Table loading/error/data state setter
 * @param setTableData - Raw API rows setter (appends new rows)
 * @param isMountedRef - Ref to check if component is still mounted
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: AppealListFilterPayload,
  setCaseAppealsState: React.Dispatch<React.SetStateAction<CaseAppealsTableState>>,
  setTableData: React.Dispatch<React.SetStateAction<CaseAppealListRow[]>>,
  isMountedRef?: React.MutableRefObject<boolean>,
): Promise<void> => {
  try {
    if (isMountedRef && !isMountedRef.current) return;

    setCaseAppealsState((prev) => ({
      ...prev,
      showInlineLoader: true,
      error: null,
    }));

    const response = await fetchAppealList(payload);

    if (isMountedRef && !isMountedRef.current) return;

    if (response.success) {
      setCaseAppealsState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });
      setTableData((prevData) => [...prevData, ...response.rows]);
    } else {
      setCaseAppealsState((prev) => ({
        ...prev,
        showInlineLoader: false,
        error: response.message || "Failed to fetch next page",
      }));
    }
  } catch (error) {
    if (isMountedRef && !isMountedRef.current) return;

    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setCaseAppealsState((prev) => ({
      ...prev,
      showInlineLoader: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForNextPagesDataFetch");
  }
};
